import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Image as ImageIcon, Link2, Lock, Plus, Star, WifiOff } from 'lucide-react'
import { useState } from 'react'

import { CreateShareLinkDialog } from './CreateShareLinkDialog'
import { EditShareLinkDialog } from './EditShareLinkDialog'
import { describeFlow } from './flows'
import type { SelectionSummary, ShareLink } from '../../api/types.gen'
import { stateOf } from './types'
import { ApiException, api } from '../../api/client'
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  ICON_STROKE,
  Skeleton,
  type Tone,
} from '../../ui'
import { formatDate } from '../billing/types'
import { formatBytes } from '../../lib/format'

const STATE_TONE: Record<ReturnType<typeof stateOf>, Tone> = {
  activo: 'positive',
  caducado: 'neutral',
  revocado: 'danger',
}

export function ShareLinksSection({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<ShareLink | null>(null)
  const [revoking, setRevoking] = useState<ShareLink | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const links = useQuery({
    queryKey: ['share-links', projectId],
    queryFn: () => api.get<{ data: ShareLink[] }>(`/projects/${projectId}/share-links`),
  })

  /*
   * Lo que han elegido los clientes.
   *
   * Va agrupado por visitante y no en un total, porque «12 favoritos» no dice
   * nada si dos personas eligieron 6 cada una: el fotógrafo necesita saber si
   * los novios ya terminaron, no cuántas marcas hay en la tabla.
   */
  const selections = useQuery({
    queryKey: ['selections', projectId],
    queryFn: () => api.get<{ data: SelectionSummary[] }>(`/projects/${projectId}/selections`),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete<{ data: unknown }>(`/share-links/${id}`),
    onSuccess: () => {
      setFailure(null)
      setRevoking(null)
      void queryClient.invalidateQueries({ queryKey: ['share-links', projectId] })
    },
    onError: (error) => {
      setRevoking(null)
      setFailure(
        error instanceof ApiException ? error.message : 'No se pudo anular el enlace.',
      )
    },
  })

  const chosen = (selections.data?.data ?? []).filter((row) => row.state === 'selected')

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-center justify-between gap-4">
        <h2 className="text-subhead font-semibold text-ink">Enlaces de entrega</h2>
        {(links.data?.data.length ?? 0) > 0 && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus size={16} strokeWidth={2} aria-hidden />
            Crear enlace
          </Button>
        )}
      </header>

      {failure !== null && (
        <Banner tone="danger" live="alert">
          {failure}
        </Banner>
      )}

      {/* Lo que ha elegido cada cliente, si alguien ha elegido algo */}
      {chosen.length > 0 && (
        <Card className="flex flex-col gap-2 p-4">
          <h3 className="flex items-center gap-2 text-label font-medium text-ink">
            <Star size={15} strokeWidth={2} fill="currentColor" aria-hidden className="text-select" />
            Lo que han elegido
          </h3>

          <ul className="flex flex-col gap-1">
            {chosen.map((row, index) => (
              <li
                key={row.visitor_id}
                className="flex items-baseline justify-between gap-3 text-body text-ink-muted"
              >
                {/* El visitor_id es un UUID interno y no significa nada para el
                    fotógrafo. Se numeran en el orden en que llegaron, que es lo
                    único que puede reconocer. */}
                <span>Cliente {index + 1}</span>
                <span className="font-medium text-ink" data-numeric>
                  {row.total} {row.total === 1 ? 'foto' : 'fotos'}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {links.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-24 w-full rounded-card" />
          <Skeleton className="h-24 w-full rounded-card" />
        </div>
      ) : links.error ? (
        <ErrorState
          icon={WifiOff}
          title="No se pudieron cargar los enlaces"
          description="Casi siempre es la conexión. Vuelve a intentarlo."
          action={
            <Button variant="secondary" onClick={() => void links.refetch()}>
              Reintentar
            </Button>
          }
        />
      ) : links.data.data.length === 0 ? (
        <EmptyState
          icon={Link2}
          title="Todavía no has compartido este proyecto"
          description="Un enlace es la entrega: se lo mandas por WhatsApp y tu cliente entra sin crear ninguna cuenta. Puedes hacer varios del mismo proyecto —uno para los novios, otro para los padrinos— cada uno con sus propias reglas."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus size={17} strokeWidth={2} aria-hidden />
              Crear el primero
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {links.data.data.map((link) => (
            <li key={link.id}>
              <LinkRow
                link={link}
                onEdit={() => setEditing(link)}
                onRevoke={() => setRevoking(link)}
              />
            </li>
          ))}
        </ul>
      )}

      {/* Montado sólo mientras está abierto: así el formulario empieza limpio
          sin tener que reiniciar campo a campo desde un efecto. */}
      {creating && (
        <CreateShareLinkDialog
          projectId={projectId}
          onClose={() => setCreating(false)}
          onCreated={() =>
            void queryClient.invalidateQueries({ queryKey: ['share-links', projectId] })
          }
        />
      )}

      {editing !== null && (
        <EditShareLinkDialog
          projectId={projectId}
          link={editing}
          onClose={() => setEditing(null)}
          onSaved={() =>
            void queryClient.invalidateQueries({ queryKey: ['share-links', projectId] })
          }
        />
      )}

      {/*
        Anular pide confirmación porque no se deshace y rompe algo que ya está
        en manos de otra persona: el enlace puede estar en el móvil de un
        cliente que lo abre esta tarde.
      */}
      <Dialog
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title="¿Anular este enlace?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRevoking(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              busy={revoke.isPending && 'Anulando…'}
              onClick={() => revoking !== null && revoke.mutate(revoking.id)}
            >
              Anular enlace
            </Button>
          </>
        }
      >
        <p className="text-body text-ink-muted">
          {revoking?.label !== null && revoking?.label !== undefined
            ? `«${revoking.label}» dejará de funcionar al instante. `
            : 'Dejará de funcionar al instante. '}
          Si tu cliente lo tiene guardado, verá que ya no está disponible. No se puede deshacer, pero
          puedes crear otro enlace nuevo cuando quieras.
        </p>
        <p className="mt-3 text-body text-ink-muted">
          Tus archivos no se tocan: sigue siendo el mismo proyecto.
        </p>
      </Dialog>
    </section>
  )
}

function LinkRow({
  link,
  onEdit,
  onRevoke,
}: {
  link: ShareLink
  onEdit: () => void
  onRevoke: () => void
}) {
  const state = stateOf(link)
  const flow = describeFlow(link.permissions, link.download_scope)
  const active = state === 'activo'

  return (
    <Card className="flex flex-wrap items-start justify-between gap-3 p-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-label font-medium text-ink">
            {link.label ?? 'Enlace sin nombre'}
          </span>
          <Badge tone={STATE_TONE[state]}>{state}</Badge>
          {link.requires_password && (
            <span className="flex items-center gap-1 text-caption text-ink-subtle">
              <Lock size={12} strokeWidth={2} aria-hidden />
              con contraseña
            </span>
          )}
        </div>

        <p className="mt-1 text-body text-ink-muted">
          {/* Un enlace creado por la API puede no encajar en ningún flujo. Se
              dice, en vez de etiquetarlo con el más parecido. */}
          {flow?.summary ?? 'Combinación personalizada de permisos.'}
          {link.max_selections !== null && ` Hasta ${link.max_selections} fotos.`}
          {/*
            El cupo de recepción no se podía ver en ninguna parte, y es el
            único número de un enlace que decide cuánto del plan del estudio
            puede gastar quien tenga la dirección. No se puede cambiar después
            —los permisos y los cupos son inmutables a propósito—, así que
            enseñarlo es lo único que permite comprobar que se creó bien.
          */}
          {link.max_upload_bytes !== null &&
            ` Puede mandarte hasta ${formatBytes(link.max_upload_bytes)}.`}
        </p>

        <p className="mt-1.5 text-caption text-ink-subtle" data-numeric>
          {/* El prefijo es lo único que queda del token, y sirve para saber cuál
              es cuál cuando hay varios sin nombre. */}
          {link.token_prefix}…
          {link.expires_at !== null && ` · caduca el ${formatDate(link.expires_at)}`}
          {link.download_count > 0 && ` · ${link.download_count} descargas`}
        </p>
      </div>

      <div className="flex shrink-0 gap-1">
        {/*
          «Portada y saludo» y no «Editar»: es literalmente lo único que se
          puede cambiar, y un botón que diga «editar» hace que el estudio lo
          abra esperando mover el cupo o los permisos —que son inmutables— y
          lo cierre sin encontrarlos.

          Sale también en los caducados: la caducidad se deshace moviendo la
          fecha del proyecto, así que ese enlace puede volver. En los anulados
          no, porque anular no se deshace.
        */}
        {state !== 'revocado' && (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <ImageIcon size={15} strokeWidth={ICON_STROKE} aria-hidden />
            Portada y saludo
          </Button>
        )}

        {active && (
          <Button variant="ghost" size="sm" onClick={onRevoke}>
            Anular
          </Button>
        )}
      </div>
    </Card>
  )
}
